import { timer } from 'd3-timer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getDifficulty } from '../configs/game';
import { Enemy } from '../entities/Enemy';
import { Entity } from '../entities/Entity';
import { Player } from '../entities/Player';
import { collidesWithEntity } from '../utils/collision';
import { gameSize } from '../utils/gameSize';
import { random } from '../utils/random';
import { renderResource } from '../utils/resources';
import { useGameStore } from './useGameStore';
import { useKeyEvents } from './useKeyEvents';
import { useMouseMovement } from './useMouseMovement';

const BASE_COLUMN_SIZE = 100;

const player = new Player();
let enemies: Enemy[] = [];
let lastElapsed = 0;
let lastAddedEnemyTime = 0;
let cachedPoints = 0;

export function useGameState() {
  const [addPoints, decreaseLife] = useGameStore(
    useCallback((state) => [state.addScore, state.decreaseLife], [])
  );
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isKeyPressed = useKeyEvents();
  const getMouseDirection = useMouseMovement();

  const positions = useMemo(() => {
    const cols = Math.floor(window.innerWidth / BASE_COLUMN_SIZE);
    const size = window.innerWidth / cols;
    const positions = new Array(cols).fill(null).map((_, i) => i * size);
    positions.shift();
    positions.pop();

    return positions;
  }, []);

  useEffect(() => {
    lastElapsed = 0;
    lastAddedEnemyTime = 0;
    cachedPoints = 0;
    enemies = [];
  }, []);

  useEffect(() => {
    function addEnemy() {
      const takenPositions = enemies.map((e) => e.x);
      const freePositions = positions.filter(
        (pos) => !takenPositions.includes(pos)
      );
      if (freePositions.length === 0) {
        return;
      }
      const y = freePositions[random(0, freePositions.length - 1)];
      enemies = [...enemies, new Enemy(y)];
    }

    function removeEnemy(index: number) {
      enemies.splice(index, 1);
    }

    function getCanvasSize(): [number, number] {
      const canvas = canvasRef.current;
      const { width = 0, height = 0 } = canvas || {};

      return [width, height];
    }

    function renderEntity(entity: Entity, ctx: CanvasRenderingContext2D) {
      ctx.save();
      ctx.translate(gameSize(entity.position[0]), gameSize(entity.position[1]));
      renderResource(entity.resource, ctx, entity.width);
      ctx.restore();
    }

    function render() {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) {
        return;
      }
      const [width, height] = getCanvasSize();

      ctx.clearRect(0, 0, width, height);

      // Player
      renderEntity(player, ctx);

      // Enemies
      for (let i = 0; i < enemies.length; i++) {
        renderEntity(enemies[i], ctx);
      }
    }

    function update(elapsed: number) {
      const dt = (elapsed - lastElapsed) / 1000.0;
      const [width, height] = getCanvasSize();

      const seconds = elapsed / 1000;
      const difficulty = getDifficulty(cachedPoints);

      if (
        enemies.length < difficulty.maxEnemies &&
        seconds - lastAddedEnemyTime > difficulty.enemyTimer &&
        random(0, seconds) < seconds / difficulty.spawnProbability
      ) {
        addEnemy();
        lastAddedEnemyTime = seconds;
      }

      if (enemies.length === 0) {
        addEnemy();
      }

      for (let i = 0; i < enemies.length; i++) {
        const enemy = enemies[i];
        const [, y] = enemy.move(dt);
        if (gameSize(y + enemy.height) >= height) {
          removeEnemy(i);
          decreaseLife();
        }
        if (collidesWithEntity(player, enemies[i])) {
          addPoints(enemies[i].points);
          cachedPoints += enemies[i].points;
          removeEnemy(i);
        }
      }

      const [mouseDirection, mouseX] = getMouseDirection(
        player.x + player.width / 2
      );
      const moveToX =
        mouseX === null ? null : Math.round(mouseX - player.width / 2);

      if (
        (isKeyPressed('left') || mouseDirection === 'left') &&
        player.position[0] > 0
      ) {
        player.move(-dt, moveToX);
      } else if (
        (isKeyPressed('right') || mouseDirection === 'right') &&
        player.position[0] < width - player.width
      ) {
        player.move(dt, moveToX);
      }

      render();

      lastElapsed = elapsed;
    }

    const time = timer(update);
    return () => time.stop();
  }, [addPoints, decreaseLife, getMouseDirection, isKeyPressed, positions]);

  return [canvasRef];
}
